// card #3200 - the 5s change check (score-api #3199's GET /show/:id/changes).
//
// ONE cheap ask per tick for the active show returns `{ showVersion, classes,
// pollMs }`. This module is the pure decision: compare it to the last values
// seen, then re-pull ONLY what moved:
//   - a class counter that differs  -> that class's rides, one ridesByClass
//     Query, into IndexedDB (pullClass);
//   - `showVersion` that differs    -> the show's judges (pullJudges).
// Nothing moved -> nothing else is requested. The timer/visibility/online
// plumbing lives in hooks/useShowChangeCheck.js; the engine (sync.js) owns the
// baselines and wires the two pull functions in.
//
// Baselines. `{ showVersion, classes: { [classId]: counter } }`, seeded by the
// hydrate walk BEFORE it pulls a show (so a write that lands mid-pull is seen
// as a move on the next check, never lost). A counter the server omits is 0
// (the server's contract: a class that never changed since #3199 shipped is
// absent). A baseline entry of PULL_AGAIN means "that class's last pull
// failed": it never equals a real counter, so the next check re-pulls it.
//
// Owed once more (#3252). ridesByClass is a GSI (eventually consistent) and
// the counter is bumped by a separate async stream, so the Query that follows
// a counter move can return rows from BEFORE the write that moved it. Adopting
// the new counter right then would leave a quiet class stale until some other
// write moved it (a Withdrawn on a class's last ride never would). So a class
// pulled BECAUSE its counter moved is also recorded in `baseline.owed`
// (`{ [classId]: counter }`): the next check pulls it exactly once more, then
// settles. One extra Query per move, no loop, no new API.
//
// With NO baseline for the show (hydrate could not read the endpoint) the
// first answer is ADOPTED as the baseline and nothing is pulled: the first
// check after the #3199 deploy would otherwise see every class that ever
// changed as new and fan out one Query per class from every tablet at once.
export const PULL_AGAIN = -1;

export const DEFAULT_POLL_MS = 5000;
// The server floors its own value at 1000; mirror it so a bad value can never
// turn the check into a hot loop.
export const MIN_POLL_MS = 1000;

export function normalizePollMs(pollMs) {
  const n = Number(pollMs);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_POLL_MS;
  return Math.max(MIN_POLL_MS, Math.floor(n));
}

const counterOf = (classes, classId) => {
  const n = Number(classes?.[classId]);
  return Number.isFinite(n) ? n : 0;
};

/** Normalise a raw endpoint answer into a baseline-shaped object. */
export function toBaseline(changes) {
  const classes = {};
  for (const id of Object.keys(changes?.classes ?? {})) {
    classes[id] = counterOf(changes.classes, id);
  }
  const showVersion = Number(changes?.showVersion);
  return { showVersion: Number.isFinite(showVersion) ? showVersion : 0, classes };
}

/**
 * @param {string} showId
 * @param {AbortSignal} [signal]
 * @param {object} deps
 * @param {(showId: string, signal?: AbortSignal) => Promise<object>} deps.getShowChanges
 * @param {(showId: string) => object|undefined} deps.getBaseline
 * @param {(showId: string, baseline: object) => void} deps.setBaseline
 * @param {(classId: string, signal?: AbortSignal) => Promise<boolean>} deps.pullClass
 *   resolves true when the class's rides were written to the cache
 * @param {(showId: string, signal?: AbortSignal) => Promise<boolean>} deps.pullJudges
 * @returns {Promise<{ pollMs: number, pulledClasses: string[], pulledJudges: boolean }>}
 *   rejects when the endpoint itself cannot be read (the caller keeps painting
 *   from the cache and just asks again next tick)
 */
export async function runChangeCheck(showId, signal, deps) {
  const changes = await deps.getShowChanges(showId, signal);
  const pollMs = normalizePollMs(changes?.pollMs);
  const next = toBaseline(changes);
  const prev = deps.getBaseline(showId);
  if (!prev) {
    deps.setBaseline(showId, next);
    return { pollMs, pulledClasses: [], pulledJudges: false };
  }

  // The new baseline starts as what the server said; a pull that fails (or is
  // aborted) puts its sentinel / old value back, so it is retried next tick.
  const settled = { showVersion: next.showVersion, classes: { ...next.classes } };
  const pulledClasses = [];
  let pulledJudges = false;

  // A PULL_AGAIN baseline entry never equals a real counter (>= 0), so a class
  // whose last pull failed lands in this list again without special-casing.
  const prevOwed = prev.owed ?? {};
  const owed = {};
  const movedClassIds = Object.keys({ ...prev.classes, ...next.classes }).filter(
    (id) => counterOf(next.classes, id) !== counterOf(prev.classes, id),
  );
  // Classes owed their one extra pull that did not also move this tick.
  const recheckIds = Object.keys(prevOwed).filter((id) => !movedClassIds.includes(id));
  for (const classId of [...movedClassIds, ...recheckIds]) {
    if (signal?.aborted) {
      settled.classes[classId] = counterOf(prev.classes, classId);
      // Carry the debt over so an abort never forgives it.
      if (classId in prevOwed) owed[classId] = prevOwed[classId];
      continue;
    }
    let ok = false;
    try {
      ok = (await deps.pullClass(classId, signal)) === true;
    } catch {
      ok = false;
    }
    if (!ok) {
      settled.classes[classId] = PULL_AGAIN;
      continue;
    }
    pulledClasses.push(classId);
    // The extra pull is this class's second read of the SAME counter: that
    // settles it. Anything else (a fresh move, or a move since the debt was
    // taken) is a first read of that counter and is owed one more.
    const counter = counterOf(next.classes, classId);
    if (prevOwed[classId] !== counter) owed[classId] = counter;
  }
  if (next.showVersion !== prev.showVersion) {
    let ok = false;
    if (!signal?.aborted) {
      try {
        ok = (await deps.pullJudges(showId, signal)) === true;
      } catch {
        ok = false;
      }
    }
    pulledJudges = ok;
    // Keep the OLD version on a failed pull so the next tick tries again.
    if (!ok) settled.showVersion = prev.showVersion;
  }

  if (Object.keys(owed).length > 0) settled.owed = owed;
  deps.setBaseline(showId, settled);
  return { pollMs, pulledClasses, pulledJudges };
}
